import React, { useContext, useState } from 'react';
import WaiverStatusBox from './WaiverStatusBox';
import WaiverDateSelector from './editor/WaiverDateSelector';
import WaiverTextField from './editor/WaiverTextField';
import WaiverCategorySelector from './editor/WaiverCategorySelector';
import YesOrNoSelector from './editor/YesOrNoSelector';
import AirlineRelationships from './editor/AirlineRelationships';
import FlightSpecificCriteria from './editor/FlightSpecificCriteria';
import WaiverLinks from './editor/WaiverLinks';
import { WaiverContext } from './WaiverEditConfigView';
import { SuggestCountry } from '../reference/AutoSuggests';
import { SuggestCountryProps } from '../../js/headersegment/interfaces';
import { addAlert, removeAllAlerts } from '../../js/configTdm';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const WaiverEditor: React.FC = () => {
  const {
    isExistingWaiver,
    waiver,
    setWaiver,
    waiverVersion,
    allWaiverVersions,
    authorizedAirlineCodes,
    changeWaiverVersion,
    changeIssuingAirline,
    setStartDate,
    setEndDate,
    shouldShowTicketStocks,
    shouldShowNotificationId,
    shouldShowComments,
    shouldShowAirlineCodes,
    airportsByAirport,
    airportsByCountry,
    setNewCountry,
    addCountry,
    shouldShowAirlineRelationships,
    updateSpecificFlights,
    shouldShowWaiverCode,
    shouldShowEndorsementBoxPolicy,
    shouldShowCabinToCabin,
    isChangeFeeCodeSupported,
    documentLink,
    removeDocument,
    canDeleteWaiver,
    deleteWaiver,
    cancelChanges,
    formHasNoChanges,
    submit,
    waiverConfig,
    editAirports,
    hasWaiverDocument,
    handleFileChange,
    updateWaiverField
  } = useContext<any>(WaiverContext);
  const [tooltipField, setTooltipField] = useState<string>('');
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const validateForm = () => {
    const errors = [];
    const errorFields = []

    // Check if publishedDate is filled
    if (!waiver.publishedDate) {
      errorFields.push('publishedDate');
      errors.push('Published Date is required.');
    }
    if (!waiver.name || waiver.name.trim() === '') {
      errorFields.push('waiverName');
      errors.push('Waiver Name is required.');
    }
    if (!waiver.category) {
      errorFields.push('waiverCategory');
      errors.push('Waiver Category is required.');
    }
    if (!waiver.firstEffectiveDate) {
      errorFields.push('firstImpactedTravelDate');
      errors.push('First Impacted Travel Date is required.');
    }
    if (!waiver.lastEffectiveDate) {
      errorFields.push('lastImpactedTravelDate');
      errors.push('Last Impacted Travel Date is required.');
    }
    if (!waiver.ticketStocks) {
      errorFields.push('ticketStocks');
      errors.push('Ticket Stock(s) is required.');
    }
    if (!waiver.airportCodes && airportsByAirport()) {
      errorFields.push('affectedAirports');
      errors.push('Affected Airports is required.');
    }
    if (!waiver.countryCodes && airportsByCountry()) {
      errorFields.push('affectedCountries');
      errors.push('Affected Countries is required.');
    }
    setTooltipField(errorFields[0]);
    setErrorFields(errorFields);

    // Add more validation checks for other required fields if needed

    return errors;
  };

  const handleSubmit = () => {
    const errors = validateForm();
    removeAllAlerts();
    if (errors.length > 0) {
      // Display errors to the user
      addAlert(`Please fix the following errors:\n${errors.join('\n')}`);
      return;
    }

    // Proceed with form submission
    console.log('Form submitted successfully!');
    submit(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string) => {
    if (e.target.value === '') {
      !errorFields.includes(fieldName) && setErrorFields([...errorFields, fieldName]);
    } else {
      setErrorFields(errorFields.filter(field => field !== fieldName));
    }
  }

  return (
    <div className="row waiver-editor">
      <div className="row waiver-title">
        <div className="col-md-12">
          <div className="col-md-6 title">
            {isExistingWaiver() ? 'Edit' : 'Add'} Waiver
            <span className="required-field-note"> (* required field)</span>
          </div>
          <div className="col-md-6">
            {isExistingWaiver() && (
              <div className="version-select form-group">
                <label htmlFor="waiverVersion">Version</label>
                <select
                  className="dropdown-toggle version-dropdown form-control"
                  id="waiverVersion"
                  name="waiver-version"
                  value={waiverVersion}
                  onChange={(e: any) => {
                    changeWaiverVersion(+e.target.value);
                  }}
                >
                  {allWaiverVersions.map((waivey: any) => (
                    <option key={waivey.version} value={waivey.version}>
                      {waivey.versionDescription}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      <form
        className="waiver-form form-inline"
        name="waiverform"
        role="form"
      >
        <div className="col-md-12 waiver-section" id="waiver-header">
          <div className="col-md-12 no-padding waiver-section-header">
            <div className="col-md-6" id="waiver-header-left">
              <div className="form-group">
                <label htmlFor="waiverIssuedAirline">Issuing Airline*:</label>
                <select
                  className="form-control dropdown-toggle issuing-airline"
                  id="waiverIssuedAirline"
                  name="waiver-issued-airline"
                  value={waiver.issuingAirlineCode}
                  required
                  onChange={(e) => {
                    setWaiver({ ...waiver, issuingAirlineCode: e.target.value });
                    changeIssuingAirline(e.target.value);
                  }}
                >
                  {authorizedAirlineCodes.map((airlineCode: any) => (
                    <option key={airlineCode} value={airlineCode}>
                      {airlineCode}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-6" id="waiver-header-right">
              {isExistingWaiver() && (
                <WaiverStatusBox waiver={waiver} readOnly={false} />
              )}
            </div>
          </div>

          {waiverConfig.entryInstructions && (
            <div className="col-md-12">
              <div className="alert alert-info">
                <div dangerouslySetInnerHTML={{ __html: waiverConfig.entryInstructions }} />
              </div>
            </div>
          )}

          <div className="col-md-12 no-padding" id="waiver-body">
            <div className="col-md-6" id="waiver-body-left">
              <WaiverDateSelector
                label="Issued/Updated"
                value={waiver.publishedDate}
                onChange={(date) => { updateWaiverField('publishedDate', date); setErrorFields(errorFields.filter(field => field !== 'publishedDate')); }}
                required
                placeholder='YYYY-MM-DD'
                errorTooltip={tooltipField === 'publishedDate'}
                onClick={() => setTooltipField('')}
                onBlur={(e) => handleBlur(e, 'publishedDate')}
                className={errorFields.includes('publishedDate') ? 'ng-invalid ng-dirty' : ''}
              />
              <WaiverTextField
                label="Waiver Name"
                placeholder="Example: France ATC Strike Update 3"
                value={waiver.name}
                onChange={(value: any) => { updateWaiverField('name', value); setErrorFields(errorFields.filter(field => field !== 'waiverName')); }}
                required
                errorTooltip={tooltipField === 'waiverName'}
                onClick={() => setTooltipField('')}
                onBlur={(e) => handleBlur(e, 'waiverName')}
                className={errorFields.includes('waiverName') ? 'ng-invalid ng-dirty' : ''}
              />
              <WaiverCategorySelector
                label="Waiver Category"
                value={waiver.category}
                onChange={(category) => updateWaiverField('category', category)}
                errorTooltip={tooltipField === 'waiverCategory'}
                onClick={() => setTooltipField('')}
              />
              <WaiverTextField
                large
                label="Description"
                value={waiver.description}
                onChange={(value: any) => updateWaiverField('description', value)}
              />
              <WaiverDateSelector
                label="First Impacted Travel Date"
                value={waiver.firstEffectiveDate}
                onChange={(value: any) => { setStartDate(value); setErrorFields(errorFields.filter(field => field !== 'firstImpactedTravelDate')); }}
                required
                placeholder='YYYY-MM-DD'
                errorTooltip={tooltipField === 'firstImpactedTravelDate'}
                onClick={() => setTooltipField('')}
                onBlur={(e) => handleBlur(e, 'firstImpactedTravelDate')}
                className={errorFields.includes('firstImpactedTravelDate') ? 'ng-invalid ng-dirty' : ''}
              />
              <WaiverDateSelector
                label="Last Impacted Travel Date"
                value={waiver.lastEffectiveDate}
                onChange={(value: any) => { setEndDate(value); setErrorFields(errorFields.filter(field => field !== 'lastImpactedTravelDate')); }}
                required
                placeholder='YYYY-MM-DD'
                errorTooltip={tooltipField === 'lastImpactedTravelDate'}
                onClick={() => setTooltipField('')}
                onBlur={(e) => handleBlur(e, 'lastImpactedTravelDate')}
                className={errorFields.includes('lastImpactedTravelDate') ? 'ng-invalid ng-dirty' : ''}
              />
              <WaiverDateSelector
                label="Ticketed On/After"
                value={waiver.ticketedOnAfter}
                onChange={(date) => updateWaiverField('ticketedOnAfter', date)}
                placeholder={'YYYY-MM-DD'}
                errorTooltip={false}
              />
              <WaiverDateSelector
                label="Ticketed On/Before"
                value={waiver.ticketedOnBefore}
                onChange={(date) => updateWaiverField('ticketedOnBefore', date)}
                placeholder={'YYYY-MM-DD'}
                errorTooltip={false}
              />
              <WaiverDateSelector
                label="Reissue Ticket On/Before"
                value={waiver.reIssuedTicketOnBefore}
                onChange={(date) => updateWaiverField('reIssuedTicketOnBefore', date)}
                placeholder={'YYYY-MM-DD'}
                errorTooltip={false}
              />
              <WaiverTextField
                condition={shouldShowTicketStocks()}
                label="Ticket Stock(s)"
                placeholder="Example: 005, 016"
                value={waiver.ticketStocks}
                onChange={(value: any) => { updateWaiverField('ticketStocks', value); setErrorFields(errorFields.filter(field => field !== 'ticketStocks')); }}
                required
                tooltip={waiver.ticketStockInstructions}
                tooltipTitle="Suggestions"
                errorTooltip={tooltipField === 'ticketStocks'}
                onClick={() => setTooltipField('')}
                onBlur={(e) => handleBlur(e, 'ticketStocks')}
                className={errorFields.includes('ticketStocks') ? 'ng-invalid ng-dirty' : ''}
              />
              <WaiverDateSelector
                label="First New Travel Date"
                value={waiver.firstNewTravelDate}
                onChange={(date) => updateWaiverField('firstNewTravelDate', date)}
                placeholder={'YYYY-MM-DD'}
                errorTooltip={false}
              />
              <WaiverDateSelector
                label="Last New Travel Date"
                value={waiver.lastNewTravelDate}
                onChange={(date) => updateWaiverField('lastNewTravelDate', date)}
                placeholder={'YYYY-MM-DD'}
                errorTooltip={false}
              />
              <WaiverTextField
                large
                label="New Travel Dates Details"
                value={waiver.newTravelDates}
                onChange={(value: any) => updateWaiverField('newTravelDates', value)}
              />
              <YesOrNoSelector
                label="Refunds Applicable"
                value={waiver.refundsApplicable}
                onChange={(value) => updateWaiverField('refundsApplicable', value)}
              />
              <WaiverTextField
                condition={shouldShowNotificationId()}
                label="Notification ID"
                value={waiver.changeEventId}
                onChange={(value: any) => updateWaiverField('changeEventId', value)}
              />
              <WaiverTextField
                condition={shouldShowComments()}
                large
                label="Internal-only Comments"
                value={waiver.comments}
                onChange={(value: any) => updateWaiverField('comments', value)}
              />
            </div>

            <div className="col-md-6" id="waiver-body-right">
              {shouldShowAirlineCodes() && (
                <div className="field-group">
                  <div className="form-group input-container-wrapper">
                    <label>Affected Regions:</label>
                    <div className="input-container">
                      <div className="btn-group">
                        <button type='button'
                          className={`btn btn-primary ${waiver.airportSelectionType === 'byAirport' ? 'active' : ''}`}
                          onClick={() => updateWaiverField('airportSelectionType', 'byAirport')}
                        >
                          By Airports
                        </button>
                        <button type='button'
                          className={`btn btn-primary ${waiver.airportSelectionType === 'byCountry' ? 'active' : ''}`}
                          onClick={() => updateWaiverField('airportSelectionType', 'byCountry')}
                        >
                          By Countries
                        </button>
                        <button type='button'
                          className={`btn btn-primary ${waiver.airportSelectionType === 'systemWide' ? 'active' : ''}`}
                          onClick={() => updateWaiverField('airportSelectionType', 'systemWide')}
                        >
                          System-Wide
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-group input-container-wrapper">
                    <label>Domestic or International Only:</label>
                    <div className="input-container">
                      <div className="btn-group">
                        <button type='button'
                          className={`btn btn-primary ${waiver.domesticOrInternationalFlights === 'domesticFlights' ? 'active' : ''}`}
                          onClick={() => updateWaiverField('domesticOrInternationalFlights', 'domesticFlights')}
                        >
                          Domestic Flights
                        </button>
                        <button type='button'
                          className={`btn btn-primary ${waiver.domesticOrInternationalFlights === 'internationalFlights' ? 'active' : ''}`}
                          onClick={() => updateWaiverField('domesticOrInternationalFlights', 'internationalFlights')}
                        >
                          International Flights
                        </button>
                        <button type='button'
                          className={`btn btn-primary ${waiver.domesticOrInternationalFlights === 'allFlights' ? 'active' : ''}`}
                          onClick={() => updateWaiverField('domesticOrInternationalFlights', 'allFlights')}
                        >
                          All Flights
                        </button>
                      </div>
                    </div>
                  </div>

                  {airportsByAirport() && (
                    <div className="form-group text-area">
                      <label htmlFor="waiverAffectedAirportsInput">
                        Affected Airports*:
                      </label>
                      <OverlayTrigger delay={{ hide: 450, show: 300 }} show={tooltipField === 'affectedAirports'} placement="bottom"
                        overlay={(props) => (
                          <>
                            <Tooltip {...props} bsPrefix='waiver-error-tooltip popover'>
                              <div className="popover-inner">
                                <div className="popover-content">
                                  <span className="sprites sprite-details-selected-24"></span>Please fill this field.
                                </div>
                              </div>
                            </Tooltip>
                          </>
                        )}><textarea
                          className={`form-control airportEditField ${errorFields.includes('affectedAirports') ? 'ng-invalid ng-dirty' : ''}`}
                          id="waiverAffectedAirportsInput"
                          name="waiver-affected-airports"
                          onClick={() => { editAirports(); setTooltipField(''); setErrorFields(errorFields.filter(field => field !== 'affectedAirports')); }}
                          value={waiver.airportCodes}
                          required={airportsByAirport()}
                          readOnly
                          onBlur={(e) => handleBlur(e, 'affectedAirports')}
                        ></textarea></OverlayTrigger>
                    </div>
                  )}

                  {airportsByCountry() && (
                    <div className="form-group mini-section">
                      <label htmlFor="waiverCountriesInput">
                        Affected Countries*:
                      </label>
                      <div className="section-content">
                        <OverlayTrigger delay={{ hide: 450, show: 300 }} show={tooltipField === 'affectedCountries'} placement="bottom"
                          overlay={(props) => (
                            <>
                              <Tooltip {...props} bsPrefix='waiver-error-tooltip popover'>
                                <div className="popover-inner">
                                  <div className="popover-content">
                                    <span className="sprites sprite-details-selected-24"></span>Please fill this field.
                                  </div>
                                </div>
                              </Tooltip>
                            </>
                          )}><input
                            type="text"
                            className={`form-control section-item uppercase ${errorFields.includes('affectedCountries') ? 'ng-invalid ng-dirty' : ''}`}
                            id="waiverCountriesInput"
                            name="waiver-affected-countries"
                            placeholder="Example: US or US, CA"
                            value={waiver.countryCodes}
                            required={airportsByCountry()}
                            onChange={(e) => { setTooltipField(''); updateWaiverField('countryCodes', e.target.value); setErrorFields(errorFields.filter(field => field !== 'affectedCountries')); }}
                            onBlur={(e) => handleBlur(e, 'affectedCountries')}
                          /></OverlayTrigger>
                        <SuggestCountry callback={(item: SuggestCountryProps) => {
                          setNewCountry(item);
                          addCountry(item);
                        }}></SuggestCountry>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {shouldShowAirlineRelationships() && (
                <div className="field-group">
                  <AirlineRelationships
                    label="Applicable Airline Relationships"
                    addButtonLabel="Add Relationship"
                    relationships={waiver.airlineRelationships}
                    onChange={(value) =>
                      updateWaiverField('airlineRelationships', value.value)
                    }
                  />
                </div>
              )}

              <div className="field-group">
                <FlightSpecificCriteria
                  label="Applicable Specific Flights"
                  addButtonLabel="Add Flight"
                  criteria={waiver.specificFlights}
                  onChange={(value) => updateSpecificFlights(value)}
                  // name="flight-specific-criteria"
                  readOnly={false}
                />
              </div>

              <WaiverTextField
                condition={shouldShowWaiverCode()}
                label="Waiver Code"
                placeholder="Example: 1A2B3C4D"
                value={waiver.code}
                onChange={(value: any) => updateWaiverField('code', value)}
              />
              <WaiverTextField
                condition={shouldShowEndorsementBoxPolicy()}
                large
                label="Endorsement Box Policy"
                value={waiver.endorsementBoxPolicy}
                onChange={(value: any) =>
                  updateWaiverField('endorsementBoxPolicy', value)
                }
              />
              <WaiverTextField
                condition={shouldShowCabinToCabin()}
                label="Cabin to Cabin Code"
                placeholder="Example: 1A2B3C4D"
                value={waiver.codeForRebooking}
                onChange={(value: any) =>
                  updateWaiverField('codeForRebooking', value)
                }
              />
              <WaiverTextField
                condition={isChangeFeeCodeSupported()}
                label="Waive Change Fee Code"
                placeholder="Example: 1A2B3C4D"
                value={waiver.codeForChangeFee}
                onChange={(value: any) =>
                  updateWaiverField('codeForChangeFee', value)
                }
              />

              <WaiverTextField
                large
                label="OSI Requirements"
                value={waiver.osiRequirements}
                onChange={(value: any) =>
                  updateWaiverField('osiRequirements', value)
                }
              />
              <WaiverTextField
                large
                label="Additional Info"
                value={waiver.additionalInfo}
                onChange={(value: any) =>
                  updateWaiverField('additionalInfo', value)
                }
              />

              <div className="field-group">
                <div className="form-group mini-section">
                  <label>Travel Waiver PDF:</label>
                  <div className="waiver-item-list section-content">
                    {hasWaiverDocument() ? (
                      <div className="form-control section-item waiver-item">
                        <a
                          title="View Document"
                          target="_blank"
                          href={documentLink()}
                        >
                          {waiver.documentName}
                        </a>
                        <a onClick={removeDocument} className="remove-item">
                          X
                        </a>
                      </div>
                    ) : (
                      <div>
                        <label
                          htmlFor="waiverDocumentInput"
                          className="btn btn-default waiver-button action-button"
                        >
                          Upload Document
                        </label>
                        <input
                          id="waiverDocumentInput"
                          className="document-uploader-input"
                          type="file"
                          accept="application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.oasis.opendocument.text,application/rtf,image/png,image/jpg,image/jpeg,image/gif,image/svg+xml,image/tiff,image/webp,image/avif,image/svg+xml"
                          onChange={handleFileChange}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="field-group">
                <WaiverLinks
                  label="Policy URLs"
                  addButtonLabel="Add Policy Link"
                  links={waiver.policyLinks}
                  onChange={(value) => updateWaiverField('policyLinks', value)}
                />
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="col-md-6"></div>
            <div className="col-md-6">
              <hr className="waiver-half-section-separator" />
              <div className="form-buttons">
                {canDeleteWaiver() && (
                  <button
                    className="btn btn-default waiver-button modal-btn-margin-right"
                    type="button"
                    id="deleteWaiverButton"
                    onClick={deleteWaiver}
                  >
                    Delete Waiver
                  </button>
                )}
                <button
                  className="btn btn-default waiver-button modal-btn-margin-right"
                  type="button"
                  id="cancelWaiverButton"
                  onClick={cancelChanges}
                >
                  Cancel Changes
                </button>
                <button
                  type="button"
                  className="btn btn-default waiver-button"
                  disabled={formHasNoChanges()}
                  id="saveWaiverButton"
                  onClick={() => handleSubmit()}
                >
                  {isExistingWaiver() ? 'Save Waiver Changes' : 'Save Waiver'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default WaiverEditor;